import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentUpload = ({ applicationId, educationLevel = 'bachelors', onDocumentsChange }) => {
  const [documents, setDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequiredDocuments();
  }, [educationLevel]);

  const fetchRequiredDocuments = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/required-types?education_level=${educationLevel}`
      );
      const data = await response.json();
      setRequiredDocs(data);
    } catch (error) {
      console.error('Error fetching required documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and image files (JPG, PNG) are allowed');
      return;
    }

    setUploading(docType);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/applications/${applicationId}/documents`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      
      // Add to local documents list
      setDocuments(prev => [...prev, {
        id: result.document.id,
        type: docType,
        filename: result.document.filename,
        file_size: result.document.file_size
      }]);

      toast.success(`${file.name} uploaded successfully`);
      
      if (onDocumentsChange) {
        onDocumentsChange([...documents, result.document]);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (docId, docType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/applications/${applicationId}/documents/${docId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
      
      if (onDocumentsChange) {
        onDocumentsChange(documents.filter(d => d.id !== docId));
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getUploadedDoc = (docType) => {
    return documents.find(d => d.type === docType);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const completionPercentage = () => {
    const required = requiredDocs.filter(d => d.required);
    const uploaded = required.filter(d => getUploadedDoc(d.type));
    return Math.round((uploaded.length / required.length) * 100) || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-500 text-sm">Document Completion</span>
          <span className="text-teal-600 font-medium">{completionPercentage()}%</span>
        </div>
        <Progress value={completionPercentage()} className="h-2" />
      </div>

      {/* Required Documents */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Required Documents</h3>
        
        {requiredDocs.filter(d => d.required).map((doc) => {
          const uploaded = getUploadedDoc(doc.type);
          const isUploading = uploading === doc.type;
          
          return (
            <div 
              key={doc.type}
              className={`p-4 rounded-lg border transition-colors ${
                uploaded 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-[#e9ecef]/50 border-gray-200 hover:border-teal-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {uploaded ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <FileText className="w-5 h-5 text-gray-500" />
                  )}
                  <div>
                    <p className="text-gray-800 font-medium">{doc.label}</p>
                    {uploaded && (
                      <p className="text-sm text-gray-500">
                        {uploaded.filename} ({formatFileSize(uploaded.file_size)})
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {uploaded ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(uploaded.id, doc.type)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(doc.type, e.target.files[0])}
                        disabled={isUploading}
                      />
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="border-teal-400 text-teal-600 hover:bg-teal-600/10"
                        disabled={isUploading}
                      >
                        <span>
                          {isUploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload
                            </>
                          )}
                        </span>
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Optional Documents */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Optional Documents</h3>
        
        {requiredDocs.filter(d => !d.required).map((doc) => {
          const uploaded = getUploadedDoc(doc.type);
          const isUploading = uploading === doc.type;
          
          return (
            <div 
              key={doc.type}
              className={`p-4 rounded-lg border transition-colors ${
                uploaded 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-[#e9ecef]/50 border-gray-200 hover:border-teal-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {uploaded ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <FileText className="w-5 h-5 text-gray-500" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-gray-800 font-medium">{doc.label}</p>
                      <Badge variant="outline" className="text-xs border-slate-500 text-gray-500">
                        Optional
                      </Badge>
                    </div>
                    {uploaded && (
                      <p className="text-sm text-gray-500">
                        {uploaded.filename} ({formatFileSize(uploaded.file_size)})
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {uploaded ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(uploaded.id, doc.type)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(doc.type, e.target.files[0])}
                        disabled={isUploading}
                      />
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="border-gray-300 text-gray-600 hover:bg-gray-100"
                        disabled={isUploading}
                      >
                        <span>
                          {isUploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload
                            </>
                          )}
                        </span>
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium text-blue-600 mb-1">Accepted formats</p>
            <p>PDF, JPG, PNG files up to 10MB each. Ensure all documents are clear and readable.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
